import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';
import { injectUntilDestroyed } from '../functions/inject-functions/inject-until-destroyed';

// Define the query parameters we want to track
export interface QueryParamsState {
  // Add specific query parameters here
  scenarioId: string | null;
}

// Initial state for the query parameters
const initialState: QueryParamsState = {
  scenarioId: null,
};

/**
 * Store for managing URL query parameters
 * This provides a centralized way to access and modify query parameters
 * without duplicating router logic across the application
 */
export const QueryParamsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => {
    const router = inject(Router);
    const route = inject(ActivatedRoute);

    return {
      /**
       * Set the scenario ID query parameter
       */
      setScenarioId: rxMethod<string>(
        pipe(
          tap((scenarioId: string) => {
            patchState(store, { scenarioId });

            // Update the URL
            router.navigate([], {
              relativeTo: route,
              queryParams: { scenarioId },
              queryParamsHandling: 'merge',
            });
          })
        )
      ),

      /**
       * Update the store state from the current URL query parameters
       * This is called internally when query parameters change
       */
      _updateFromQueryParams: rxMethod<Record<string, unknown>>(
        pipe(
          tap((params: Record<string, unknown>) => {
            // Extract the query parameters we care about
            const newState: Partial<QueryParamsState> = {};

            // Map URL params to state
            if ('scenarioId' in params) {
              newState.scenarioId = (params.scenarioId as string) || null;
            }

            patchState(store, newState);
          })
        )
      ),
    };
  }),
  withHooks({
    onInit: (store) => {
      const untilDestroyed = injectUntilDestroyed();
      const route = inject(ActivatedRoute);

      // Listen for query parameter changes
      route.queryParams.pipe(untilDestroyed()).subscribe((params) => {
        store._updateFromQueryParams(params);
      });
    },
  })
);
